import {
    isValidElement,
    type ReactNode,
    useCallback,
    Children,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import type IChartLegendProps from "./components/legend/type";
import type IChartProps from "./type";
import type {
    ChartLegendPosition,
    ChartContextType,
    ChartModel
} from "./type";
import stylesheet from "./stylesheet";
import ChartAxis from "./components/axis";
import ChartContent from "./components/content";
import ChartFooter from "./components/footer";
import ChartGrid from "./components/grid";
import ChartHeader from "./components/header";
import ChartLegend from "./components/legend";
import ChartPlot from "./components/plot";
import ChartRange from "./components/range";
import ChartSkeleton from "./components/skeleton";
import ChartTable from "./components/table";
import ChartTableToggle from "./components/tableToggle";
import ChartTooltip from "./components/tooltip";
import ChartValue from "./components/value";
import {
    ChartContext
} from "./context";
import Card from "../card";

const Chart: FC<IChartProps> = ({
    isTableVisible: isTableVisibleProp,
    spreadBehaviour = "baseline",
    isAnimatedOnMount = false,
    isLegendToggleable = true,
    tooltipBehaviour = "auto",
    onTableVisibleChange,
    dataState = "ready",
    isPatterned = false,
    isLoading = false,
    stateActionTitle,
    stateDescription,
    size = "medium",
    customLocalize,
    onStateAction,
    customTheme,
    description,
    stateTitle,
    children,
    config,
    footer,
    style,
    title,
    note,
    ...props
}) => {
    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const modelRef = useRef<ChartModel | undefined>(undefined);

    const [
        modelVersion,
        setModelVersion
    ] = useState<number>(0);

    const [
        hiddenKeys,
        setHiddenKeys
    ] = useState<Array<string>>([]);

    const [
        isTableVisibleState,
        setIsTableVisibleState
    ] = useState<boolean>(false);

    const [
        plotHeight,
        setPlotHeight
    ] = useState<number | undefined>(undefined);

    const model = modelRef.current;

    const hasData = !!model?.data.length;

    const isRefetching = isLoading && hasData;

    const isTableVisible = isTableVisibleProp ?? isTableVisibleState;

    const isTableDisabled = dataState !== "ready" || (isLoading && !hasData);

    const seriesKeys = model?.legendItems?.map((item) => item.key) ?? model?.seriesKeys ?? [];

    const notifyModel = useCallback(() => {
        setModelVersion((current) => current + 1);
    }, []);

    const toggleSeries = useCallback((key: string) => {
        setHiddenKeys((current) => {
            if(current.includes(key)) {
                return current.filter((item) => item !== key);
            }

            const visibleCount = seriesKeys.filter((item) => !current.includes(item)).length;

            if(visibleCount <= 1) {
                return current;
            }

            return [
                ...current,
                key
            ];
        });
    }, [
        seriesKeys.join(",")
    ]);

    const toggleTable = useCallback(() => {
        const nextValue = !isTableVisible;

        setIsTableVisibleState(nextValue);

        onTableVisibleChange?.(nextValue);
    }, [
        onTableVisibleChange,
        isTableVisible
    ]);

    const context = useMemo<ChartContextType>(() => ({
        isLegendToggleable,
        isAnimatedOnMount,
        tooltipBehaviour,
        isTableDisabled,
        customLocalize,
        isTableVisible,
        setPlotHeight,
        isRefetching,
        toggleSeries,
        customTheme,
        description,
        isPatterned,
        notifyModel,
        toggleTable,
        hiddenKeys,
        plotHeight,
        dataState,
        isLoading,
        modelRef,
        hasData,
        baseID,
        config,
        title,
        size
    }), [
        isLegendToggleable,
        isAnimatedOnMount,
        tooltipBehaviour,
        isTableDisabled,
        customLocalize,
        isTableVisible,
        toggleSeries,
        isRefetching,
        customTheme,
        description,
        isPatterned,
        notifyModel,
        toggleTable,
        plotHeight,
        hiddenKeys,
        dataState,
        isLoading,
        modelRef,
        hasData,
        baseID,
        config,
        title,
        size,
        modelVersion
    ]);

    const items = Children.toArray(children);

    const rangeElement = items.find((item) => isValidElement(item) && item.type === ChartRange);

    const valueElement = items.find((item) => isValidElement(item) && item.type === ChartValue);

    const legendElement = items.find((item) => isValidElement(item) && item.type === ChartLegend);

    const contentElements = items.filter((item) => item !== rangeElement && item !== valueElement && item !== legendElement);

    const legendPosition: ChartLegendPosition = isValidElement<IChartLegendProps>(legendElement) ? legendElement.props.position ?? "bottom" : "bottom";

    const legend: ReactNode = isTableVisible ? null : legendElement ?? <ChartLegend />;

    const isFooterVisible = !!note || !!footer || isRefetching;

    return <ChartContext.Provider
        value={context}
    >
        <Card
            {...props}
            spreadBehaviour={spreadBehaviour}
            customLocalize={customLocalize}
            customTheme={customTheme}
            style={[
                style,
                stylesheet.container
            ]}
            aria-busy={isLoading}
            role="article"
            size={size}
        >
            <ChartHeader
                isTableToggleVisible={!isTableDisabled}
                customLocalize={customLocalize}
                isRefetching={isRefetching}
                customTheme={customTheme}
                description={description}
                actions={rangeElement}
                title={title}
            />
            <ChartContent
                legendBottom={legendPosition === "bottom" ? legend : undefined}
                legendTop={legendPosition === "top" ? legend : undefined}
                stateActionTitle={stateActionTitle}
                stateDescription={stateDescription}
                customLocalize={customLocalize}
                onStateAction={onStateAction}
                customTheme={customTheme}
                stateTitle={stateTitle}
                value={valueElement}
            >
                {contentElements}
            </ChartContent>
            {
                isFooterVisible ?
                    <ChartFooter
                        customLocalize={customLocalize}
                        isRefetching={isRefetching}
                        customTheme={customTheme}
                        note={note}
                    >
                        {footer}
                    </ChartFooter>
                :
                    null
            }
        </Card>
    </ChartContext.Provider>;
};
export {
    useChartContext,
    useChartModel,
    ChartContext
} from "./context";
export {
    default as useChartSeries
} from "./useChartSeries";
export {
    default as useChartEntrance
} from "./useChartEntrance";
export {
    default as useChartValueTransition
} from "./useChartValueTransition";
export {
    getChartTooltipPosition,
    getChartStaggerProgress,
    getChartKeyboardIndex,
    getChartLabelIndexes,
    CHART_SKELETON_KINDS,
    CHART_PATTERN_ANGLES,
    getChartSeriesColor,
    getChartLinearScale,
    CHART_STAGGER_LIMIT,
    getChartTableModel,
    formatChartCompact,
    getChartCurvePath,
    getChartNiceScale,
    getChartBandIndex,
    getChartTextWidth,
    getChartAreaPath,
    getChartPlotRect,
    CHART_TICK_COUNT,
    CHART_TABLE_GAP,
    getChartBarPath,
    getChartArcPath,
    getChartPattern,
    getChartPointX,
    getChartXTicks,
    getChartBand
} from "./util";
export default Object.assign(Chart, {
    TableToggle: ChartTableToggle,
    Skeleton: ChartSkeleton,
    Content: ChartContent,
    Tooltip: ChartTooltip,
    Footer: ChartFooter,
    Header: ChartHeader,
    Legend: ChartLegend,
    Range: ChartRange,
    Table: ChartTable,
    Value: ChartValue,
    Axis: ChartAxis,
    Plot: ChartPlot,
    Grid: ChartGrid
});
